'use client'

import * as React from 'react'
import * as NavigationMenu from 'primitives/navigation-menu'
import { tw } from 'utils/classnames'

import type { NavItem } from 'cf-menu/hooks/use-nav-items'
import { useNavItems } from 'cf-menu/hooks/use-nav-items'
import { useLinkComponent, useRouterUtil } from 'cf-menu/contexts/header-config-context'
import { useSecondaryNavItems } from 'cf-menu/hooks/use-secondary-nav-items'
import { PrimarySubmenu } from 'cf-menu/components/desktop/main-navigation/primary-submenu'
import { containerClasses } from 'cf-menu/components/desktop'
import { useHeaderContext } from 'cf-menu/contexts/main-navigation-context'
import { useIsIpad } from 'cf-menu/hooks/use-is-ipad'
import type { ColorScheme } from 'cf-menu/utils/get-color-scheme'
import { getColorScheme } from 'cf-menu/utils/get-color-scheme'

export function HomepageMainNavigation({ extraNavItems }: { extraNavItems?: NavItem[] }) {
  const { pathname } = useRouterUtil()
  const baseNavItems = useNavItems()
  const navItems = React.useMemo(() => {
    if (!extraNavItems?.length) {
      return baseNavItems
    }
    const studioIndex = baseNavItems.findIndex(item => item.id === 'studio')
    const insertAt = studioIndex === -1 ? baseNavItems.length : studioIndex + 1
    return [...baseNavItems.slice(0, insertAt), ...extraNavItems, ...baseNavItems.slice(insertAt)]
  }, [baseNavItems, extraNavItems])
  const secondaryNavItems = useSecondaryNavItems()
  const {
    setMainMenuContainerRef,
    activeItem,
    activePrimaryItem,
    activeSecondaryItem,
    horizontalPosition,
  } = useHeaderContext()
  const [, setItem] = activeItem
  const [, setActivePrimaryItem] = activePrimaryItem
  const [, setActiveSecondaryItem] = activeSecondaryItem
  const [item] = activeItem
  const hasSubItems = Boolean(item?.subMenuItems?.length)
  const isIpad = useIsIpad()

  const closeMenu = React.useCallback(() => {
    const activeElement = document.activeElement as HTMLElement
    activeElement?.blur()
    setItem(null)
    setActivePrimaryItem(null)
    setActiveSecondaryItem(null)
  }, [setItem, setActivePrimaryItem, setActiveSecondaryItem])

  React.useEffect(closeMenu, [pathname, closeMenu])

  return (
    <div
      className={tw(
        'bg-cf-white relative border-b border-[#D8DAE4]',
        isIpad ? 'cf-hidden' : 'cf-hidden min-[1175px]:block',
      )}
    >
      <div
        id="main-navigation-wrapper"
        className={tw(
          containerClasses,
          'flex flex-nowrap items-center justify-between gap-6',
          'relative',
        )}
      >
        <NavigationBlock items={navItems} closeMenu={closeMenu} />
        <NavigationBlock items={secondaryNavItems} closeMenu={closeMenu} />
        {hasSubItems ? (
          <div
            id="dropdown-wrapper"
            className={tw(
              'rounded-2 z-cf-menu absolute left-0 top-full box-content overflow-hidden px-4 pb-8 pt-[1px]',
              activePrimaryItem[0]?.subMenuItems && 'w-[calc(100%-2*16px)]',
            )}
            style={{ left: activePrimaryItem[0]?.subMenuItems ? 0 : horizontalPosition }}
          >
            <div
              className={tw(
                'rounded-2 bg-cf-dreamy-cloud-400 text-cf-dark-aubergine shadow-cf-elevation-light-5 relative flex overflow-hidden',
              )}
              ref={setMainMenuContainerRef}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

function NavigationBlock({
  items,
  closeMenu,
  colors,
}: {
  items: NavItem[]
  closeMenu: () => void
  colors?: Partial<ColorScheme>
}) {
  const colorScheme = getColorScheme(colors)
  const Link = useLinkComponent()
  const { pathname } = useRouterUtil()
  const { activeItem, activePrimaryItem, activeSecondaryItem } = useHeaderContext()
  const [item, setItem] = activeItem
  const [, setActivePrimaryItem] = activePrimaryItem
  const [, setActiveSecondaryItem] = activeSecondaryItem

  return (
    <NavigationMenu.Root
      className="relative flex"
      value={item?.name || ''}
      onValueChange={value => {
        const foundItem = items.find(i => i.name === value) ?? null
        const firstPrimaryItem = foundItem?.subMenuItems?.[0] ?? null
        const firstSecondaryItem = foundItem?.subMenuItems?.[0]?.subMenuItems?.[0] ?? null
        setItem(foundItem)
        setActivePrimaryItem(firstSecondaryItem ? firstPrimaryItem : null)
        setActiveSecondaryItem(firstSecondaryItem ? firstSecondaryItem : null)
      }}
      onClickCapture={e => {
        const isOpenInNewTab =
          (e.target as HTMLElement).parentElement?.getAttribute('target') === '_blank'
        if (isOpenInNewTab) {
          setTimeout(closeMenu, 300)
        }
      }}
    >
      <NavigationMenu.List className="flex h-full items-center justify-stretch gap-2">
        {items.map((item, index) => {
          return (
            <NavigationMenu.Item key={item.id ?? item.name} value={item.name}>
              <NavigationMenu.Trigger
                style={{
                  '--text-color': colorScheme.textColor,
                  '--border-color': colorScheme.borderColor,
                  '--focus-color': colorScheme.textHoverColor,
                }}
                className={tw(
                  'text-[var(--text-color)]',
                  'data-[state=open]:border-[var(--border-color)]',
                  'data-[state=open]:text-[var(--focus-color)]',
                  'focus:border-[var(--focus-color)]',
                  'focus:text-[var(--focus-color)]',
                  'group border-b-4 border-transparent transition-colors',
                  item.link &&
                    replaceLanguageInPath(pathname) === item.link &&
                    'border-[var(--border-color)] text-[var(--text-color)]',
                )}
              >
                {item.link ? (
                  <Link
                    href={item.link}
                    target={item.target}
                    rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                    onClick={() => item.track?.()}
                    className={tw('block px-2 py-3', '2xl:text-14 text-xs leading-4 2xl:leading-4')}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <div
                    className={tw('block px-2 py-3', '2xl:text-14 text-xs leading-4 2xl:leading-4')}
                  >
                    {item.name}
                  </div>
                )}
              </NavigationMenu.Trigger>
              <PrimarySubmenu items={item.subMenuItems} colors={colorScheme} />
            </NavigationMenu.Item>
          )
        })}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  )
}

function replaceLanguageInPath(path: string) {
  const pathname = path.replace(/^\/(en|es|de|pt|fr|it|nl|pl)\//, '/')
  return pathname.endsWith('/') ? pathname : `${pathname}/`
}
